/**
 * Aura Viva — cart linking UI (group jewels into shared galleries).
 *
 * For every jewel line item in the cart (main cart page or cart drawer), this
 * script injects a colored "Gallery N" badge. Clicking the badge opens a menu
 * that lets the customer move that jewel into a different gallery group, OR
 * separate it into its own gallery.
 *
 * The cart line items must expose:
 *   - data-av-uid    = the jewel's _av_uid property
 *   - data-av-key    = the cart line item key (item.key from /cart.js)
 *   - data-av-upgrade= "true" for the Infinita upgrade line items (we skip those)
 *
 * Translations are read from a JSON script tag with id="AV_CART_LINK_I18N".
 *
 * Cart mutation pattern:
 *   - On link  → rewrite the jewel's _av_uid to the target group's uid + set tier
 *                to match the target group (esencia or infinita).
 *   - On unlink→ rewrite the jewel's _av_uid to a fresh uuid + set tier to esencia.
 *
 * The av-cart-tier-guard.js then runs (via the av:cart-changed event) and
 * cleans up: removes orphan upgrades, deduplicates upgrades within a group,
 * demotes Infinita jewels with no upgrade, etc.
 */
(function () {
  "use strict";

  if (window.__AV_CART_LINK__) return;
  window.__AV_CART_LINK__ = true;

  /* ── palette: 6 colors that work on cream backgrounds ── */
  var PALETTE = [
    "#5D8BB0", // soft blue
    "#5A7A4F", // forest green
    "#C8743C", // burnt orange
    "#B85C7A", // rose
    "#6B4F8C", // plum
    "#4A7C7E", // teal
  ];

  /* ── i18n ── */

  var i18nCache = null;
  function readI18n() {
    if (i18nCache) return i18nCache;
    var el = document.getElementById("AV_CART_LINK_I18N");
    if (!el) { i18nCache = {}; return i18nCache; }
    try { i18nCache = JSON.parse(el.textContent || "{}"); }
    catch (e) { i18nCache = {}; }
    return i18nCache;
  }

  function fmt(template, vars) {
    return String(template || "").replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, function (_, k) {
      return vars && Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : "";
    });
  }

  function t(path, vars) {
    var i18n = readI18n();
    var node = i18n;
    var keys = String(path || "").split(".");
    for (var i = 0; i < keys.length; i++) {
      if (node && typeof node === "object") node = node[keys[i]];
      else { node = null; break; }
    }
    return fmt(node || path, vars);
  }

  /* ── helpers ── */

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  function uuidv4() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    var buf = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(buf);
    buf[6] = (buf[6] & 0x0f) | 0x40;
    buf[8] = (buf[8] & 0x3f) | 0x80;
    var b = Array.from(buf, function (x) { return x.toString(16).padStart(2, "0"); }).join("");
    return b.slice(0,8) + "-" + b.slice(8,12) + "-" + b.slice(12,16) + "-" + b.slice(16,20) + "-" + b.slice(20);
  }

  async function fetchCart() {
    // Locale root (/en/, /ca/) so product titles come back in the shopper's language.
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    var r = await fetch(root + "cart.js", { headers: { Accept: "application/json" } });
    return r.json();
  }

  async function changeLineItem(key, qty, props) {
    var body = { id: key, quantity: qty };
    if (props) body.properties = props;
    var r = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      var data = await r.json().catch(function () { return {}; });
      throw new Error(data.description || data.message || "Cart change failed");
    }
    return r.json();
  }

  /* ── group map ── */

  // Maps uid → { number, color, jewelCount, hasUpgrade, jewels:[{key,title,hadOwnUpgrade}], upgrade:{key,variant_id,...}|null }
  function buildGroupMap(cart) {
    var items = cart.items || [];
    var groups = new Map();
    var order = 0;

    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var u = (it.properties && it.properties._av_uid) || "";
      if (!u) continue;
      if (!groups.has(u)) {
        groups.set(u, {
          number: ++order,
          color: PALETTE[(order - 1) % PALETTE.length],
          jewelCount: 0,
          hasUpgrade: false,
          jewels: [],
          upgrade: null,
          firstSeenIndex: i,
        });
      }
      var g = groups.get(u);
      var isUpgrade = String((it.properties || {})._av_upgrade || "").toLowerCase() === "true";
      if (isUpgrade) {
        g.hasUpgrade = true;
        g.upgrade = { key: it.key, variant_id: it.variant_id, quantity: it.quantity, properties: it.properties };
      } else {
        g.jewelCount++;
        g.jewels.push({
          key: it.key,
          title: it.product_title || it.title,
          hadOwnUpgrade: String((it.properties || {})._av_had_own_upgrade || "").toLowerCase() === "true",
        });
      }
    }
    return groups;
  }

  // Look up the Infinita upgrade variant_id by sampling any existing upgrade in the cart.
  // Used when unlink needs to add a fresh upgrade for the separated jewel.
  function findUpgradeVariantId(cart) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var isUpgrade = String((it.properties || {})._av_upgrade || "").toLowerCase() === "true";
      if (isUpgrade && it.variant_id) return it.variant_id;
    }
    return null;
  }

  async function addLineItem(variantId, quantity, properties) {
    var r = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: Number(quantity || 1), properties: properties || {} }],
      }),
    });
    if (!r.ok) {
      var data = await r.json().catch(function () { return {}; });
      throw new Error(data.description || data.message || "Add to cart failed");
    }
    return r.json();
  }

  /* ── styles (injected once) ── */

  function injectStyles() {
    if (document.getElementById("av-cart-link-styles")) return;
    var s = document.createElement("style");
    s.id = "av-cart-link-styles";
    s.textContent = [
      ".av-group-badge-container{margin-top:8px;display:inline-block;}",
      ".av-group-badge{display:inline-flex;align-items:center;gap:6px;color:#fff;padding:5px 12px;border-radius:999px;font-family:'Quattrocento Sans',sans-serif;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;border:none;cursor:pointer;transition:filter .15s,transform .05s;line-height:1;}",
      ".av-group-badge:hover:not(.av-group-badge--readonly){filter:brightness(1.1);}",
      ".av-group-badge:active:not(.av-group-badge--readonly){transform:translateY(1px);}",
      ".av-group-badge--readonly{cursor:default;opacity:.85;padding:4px 10px;font-size:10.5px;}",
      ".av-group-badge__caret{font-size:8px;opacity:.85;}",

      ".av-group-menu{position:absolute;z-index:999998;background:#EFE0C8;border-radius:12px;box-shadow:0 12px 32px rgba(44,51,47,.25);padding:6px;min-width:260px;font-family:'Quattrocento Sans',sans-serif;color:#2C332F;border:1px solid rgba(44,51,47,.08);}",
      ".av-group-menu__title{font-size:10.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;opacity:.55;padding:8px 12px 6px;}",
      ".av-group-menu__item{display:flex;align-items:center;gap:10px;width:100%;padding:9px 12px;background:transparent;border:none;border-radius:8px;cursor:pointer;font:inherit;font-size:13.5px;color:#2C332F;text-align:left;}",
      ".av-group-menu__item:hover:not([disabled]){background:rgba(44,51,47,.07);}",
      ".av-group-menu__item[disabled]{opacity:.55;cursor:default;}",
      ".av-group-menu__item--current{font-weight:700;}",
      ".av-group-menu__swatch{width:14px;height:14px;border-radius:50%;flex-shrink:0;}",
      ".av-group-menu__label{flex:1;}",
      ".av-group-menu__count{font-size:11px;opacity:.55;margin-left:auto;}",
      ".av-group-menu__hint{padding:8px 12px 10px;font-size:12px;opacity:.6;line-height:1.4;}",
      ".av-group-menu__sep{height:1px;background:rgba(44,51,47,.1);margin:4px 8px;}",

      // Cart-top "did you know you can pair jewels?" tip banner.
      // Rendered server-side in main-cart-items.liquid + snippets/cart-drawer.liquid;
      // these styles cover both contexts so the banner looks identical.
      ".av-cart-linking-tip{display:flex;align-items:center;gap:12px;margin:0 0 14px;padding:11px 14px;background:#EFE0C8;border:1px solid rgba(44,51,47,.15);border-left:4px solid #2C332F;border-radius:0 8px 8px 0;font-family:'Quattrocento Sans',sans-serif;font-size:12.5px;color:#2C332F;line-height:1.45;}",
      ".av-cart-linking-tip__icon{flex-shrink:0;font-size:15px;color:#2C332F;}",
      ".av-cart-linking-tip__text strong{font-weight:700;}",
    ].join("");
    document.head.appendChild(s);
  }

  /* ── badge rendering ── */

  // Find all AV rows (both jewels and upgrade lines).
  // The caller distinguishes via the row's data-av-upgrade attribute and
  // renders an interactive badge for jewels, a read-only "linked to" tag
  // for upgrade lines.
  function findAvRows() {
    return Array.from(document.querySelectorAll("[data-av-uid][data-av-key]"))
      .filter(function (el) {
        return !!el.getAttribute("data-av-uid");
      });
  }
  function isUpgradeRow(el) {
    return el.getAttribute("data-av-upgrade") === "true";
  }

  function getInsertionPoint(row) {
    // Slot the badge into the details column. Both cart page and drawer
    // use `.cart-item__details`.
    var details = row.querySelector(".cart-item__details");
    return details || row;
  }

  async function renderBadges() {
    var cart;
    try { cart = await fetchCart(); } catch (e) { return; }

    var groups = buildGroupMap(cart);

    // Remove any existing badges (idempotent re-render)
    Array.from(document.querySelectorAll(".av-group-badge-container")).forEach(function (c) { c.remove(); });

    var rows = findAvRows();
    if (rows.length === 0) return;

    rows.forEach(function (row) {
      var uid = row.getAttribute("data-av-uid");
      var key = row.getAttribute("data-av-key");
      var g = groups.get(uid);
      if (!g) return;

      var container = document.createElement("div");
      container.className = "av-group-badge-container";

      if (isUpgradeRow(row)) {
        // Read-only chip on the upgrade line so the customer can see WHICH
        // gallery this upgrade is paying for. No click handler, no caret —
        // it's purely a visual association.
        var chip = document.createElement("span");
        chip.className = "av-group-badge av-group-badge--readonly";
        chip.style.background = g.color;
        chip.setAttribute("aria-label", t("group.aria_label", { n: g.number }));
        chip.textContent = t("group.label", { n: g.number });
        container.appendChild(chip);
      } else {
        // Interactive badge on jewel rows — opens the move-to menu.
        var badge = document.createElement("button");
        badge.type = "button";
        badge.className = "av-group-badge";
        badge.style.background = g.color;
        badge.setAttribute("aria-label", t("group.aria_label", { n: g.number }));
        badge.setAttribute("data-av-uid", uid);
        badge.setAttribute("data-av-key", key);
        badge.innerHTML =
          '<span>' + escapeHtml(t("group.label", { n: g.number })) + '</span>' +
          '<span class="av-group-badge__caret">▼</span>';

        badge.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          openMenu(badge, key, uid, groups);
        });
        container.appendChild(badge);
      }

      getInsertionPoint(row).appendChild(container);
    });
  }

  /* ── menu (per-jewel "move to" dropdown) ── */

  function closeAnyOpenMenu() {
    Array.from(document.querySelectorAll(".av-group-menu")).forEach(function (m) { m.remove(); });
  }

  function openMenu(badge, jewelKey, currentUid, groups) {
    closeAnyOpenMenu();

    var menu = document.createElement("div");
    menu.className = "av-group-menu";

    var title = document.createElement("div");
    title.className = "av-group-menu__title";
    title.textContent = t("group.menu_title");
    menu.appendChild(title);

    // List all groups; current first
    var entries = Array.from(groups.entries());
    entries.sort(function (a, b) {
      return a[0] === currentUid ? -1 : b[0] === currentUid ? 1 : a[1].number - b[1].number;
    });

    for (var i = 0; i < entries.length; i++) {
      var u = entries[i][0];
      var g = entries[i][1];

      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "av-group-menu__item" + (u === currentUid ? " av-group-menu__item--current" : "");
      if (u === currentUid) btn.disabled = true;

      var labelText = u === currentUid
        ? t("group.menu_current", { n: g.number })
        : t("group.menu_move_to", { n: g.number });
      var countText = g.jewelCount === 1
        ? t("group.menu_jewel_count_one", { count: g.jewelCount })
        : t("group.menu_jewel_count_many", { count: g.jewelCount });

      btn.innerHTML =
        '<span class="av-group-menu__swatch" style="background:' + g.color + '"></span>' +
        '<span class="av-group-menu__label">' + escapeHtml(labelText) + '</span>' +
        '<span class="av-group-menu__count">' + escapeHtml(countText) + '</span>';

      if (u !== currentUid) {
        (function (targetUid) {
          btn.addEventListener("click", function (e) {
            e.preventDefault(); e.stopPropagation();
            closeAnyOpenMenu();
            confirmLink(jewelKey, currentUid, targetUid, groups);
          });
        })(u);
      }
      menu.appendChild(btn);
    }

    // "Move to new gallery" — only meaningful if current group has >1 jewels
    // (otherwise the jewel is already alone in its own group)
    var currentGroup = groups.get(currentUid);
    if (currentGroup && currentGroup.jewelCount > 1) {
      var sep = document.createElement("div");
      sep.className = "av-group-menu__sep";
      menu.appendChild(sep);

      var newBtn = document.createElement("button");
      newBtn.type = "button";
      newBtn.className = "av-group-menu__item";
      newBtn.innerHTML =
        '<span class="av-group-menu__swatch" style="background:#ccc;border:1px dashed #888"></span>' +
        '<span class="av-group-menu__label">' + escapeHtml(t("group.menu_move_to_new")) + '</span>';
      newBtn.addEventListener("click", function (e) {
        e.preventDefault(); e.stopPropagation();
        closeAnyOpenMenu();
        confirmUnlink(jewelKey, currentUid, groups);
      });
      menu.appendChild(newBtn);
    } else if (groups.size === 1) {
      var hint = document.createElement("div");
      hint.className = "av-group-menu__hint";
      hint.textContent = t("group.menu_only_one_gallery");
      menu.appendChild(hint);
    }

    document.body.appendChild(menu);

    // Position below badge
    var rect = badge.getBoundingClientRect();
    menu.style.top = (rect.bottom + window.scrollY + 6) + "px";
    menu.style.left = Math.min(
      rect.left + window.scrollX,
      window.scrollX + document.documentElement.clientWidth - menu.offsetWidth - 16
    ) + "px";

    // Close on outside click
    setTimeout(function () {
      function onDoc(e) {
        if (!menu.contains(e.target)) {
          menu.remove();
          document.removeEventListener("click", onDoc, true);
        }
      }
      document.addEventListener("click", onDoc, true);
    }, 0);
  }

  /* ── confirmation modals (context-aware copy) ── */

  function confirmLink(jewelKey, sourceUid, targetUid, groups) {
    var sourceG = groups.get(sourceUid);
    var targetG = groups.get(targetUid);
    var sourceLastJewel = sourceG && sourceG.jewelCount === 1;
    var sourceHasUpgrade = !!(sourceG && sourceG.hasUpgrade);
    var targetHasUpgrade = !!(targetG && targetG.hasUpgrade);

    // Decide which contextual line to show:
    //   - duplicate strip   : source has its own upgrade AND target also has one
    //                         → moving this jewel orphans source's upgrade,
    //                           guard removes it = refund.
    //   - cross-plan note   : source has upgrade, target has none
    //                         → upgrade transfers; merged group becomes Infinita.
    //   - neither           : nothing special.
    var contextHtml = "";
    if (sourceHasUpgrade && sourceLastJewel && targetHasUpgrade) {
      contextHtml = '<p>' + escapeHtml(t("link_modal.body_upgrade_note")) + '</p>';
    } else if (sourceHasUpgrade && sourceLastJewel && !targetHasUpgrade) {
      contextHtml = '<p>' + escapeHtml(t("link_modal.body_cross_plan_note")) + '</p>';
    } else if (!sourceHasUpgrade && targetHasUpgrade) {
      // Esencia jewel joining an Infinita group — also a cross-plan note from
      // the customer's perspective (their jewel is "becoming" Infinita).
      contextHtml = '<p>' + escapeHtml(t("link_modal.body_cross_plan_note")) + '</p>';
    }

    var bodyHtml =
      '<p>' + escapeHtml(t("link_modal.body")) + '</p>' +
      contextHtml +
      '<p>' + escapeHtml(t("link_modal.body_irreversible")) + '</p>';

    window.AVModal.show({
      title: t("link_modal.title"),
      bodyHtml: bodyHtml,
      cancelText: t("modal.cancel"),
      confirmText: t("link_modal.confirm"),
      variant: "primary",
      onConfirm: function () { doLink(jewelKey, sourceUid, targetUid); },
    });
  }

  function confirmUnlink(jewelKey, sourceUid, groups) {
    var sourceG = groups.get(sourceUid);
    var sourceHasUpgrade = !!(sourceG && sourceG.hasUpgrade);
    // Find the specific jewel we're unlinking — its own `_av_had_own_upgrade`
    // flag decides whether unlinking restores Infinita (paid jewel) or just
    // sets Esencia (free jewel that joined an Infinita group via link).
    var thisJewel = sourceG && sourceG.jewels.find(function (j) { return j.key === jewelKey; });
    var willRestoreInfinita = !!(thisJewel && thisJewel.hadOwnUpgrade && sourceHasUpgrade);

    // The middle warning depends on whether this specific jewel deserves
    // an Infinita restore (it was paid as Infinita originally).
    var middleHtml = willRestoreInfinita
      ? '<p>' + escapeHtml(t("unlink_modal.body_infinita_restore")) + '</p>'
      : '<p>' + escapeHtml(t("unlink_modal.body_esencia_warning")) + '</p>';

    var bodyHtml =
      '<p>' + escapeHtml(t("unlink_modal.body")) + '</p>' +
      middleHtml +
      '<p>' + escapeHtml(t("unlink_modal.body_irreversible")) + '</p>';

    window.AVModal.show({
      title: t("unlink_modal.title"),
      bodyHtml: bodyHtml,
      cancelText: t("modal.cancel"),
      confirmText: t("unlink_modal.confirm"),
      variant: willRestoreInfinita ? "primary" : "danger",
      onConfirm: function () { doUnlink(jewelKey, sourceUid); },
    });
  }

  /* ── actions ── */

  /**
   * Link: move `jewelKey` from `sourceUid` group into `targetUid` group.
   *
   * Upgrade handling:
   *   - source has its OWN upgrade (sourceJewelCount === 1) and this move
   *     orphans it → if target has no upgrade, TRANSFER source's upgrade to
   *     target (so the merged group keeps Infinita). If target has an
   *     upgrade, leave source's upgrade orphaned; the guard will strip it
   *     (and show the "duplicate Infinita removed" toast).
   *   - source has a SHARED upgrade (other jewels stay in source) → leave
   *     it alone; source group keeps its upgrade for the remaining jewels.
   */
  /**
   * Move `jewelKey` (currently in `sourceUid`) into the `targetUid` gallery,
   * keeping exactly one Infinita line per gallery. Shared by the cart badges
   * (doLink) and the add-to-cart popup question (av-tier.js). Throws on error.
   */
  async function mergeInto(jewelKey, sourceUid, targetUid) {
    var cart = await fetchCart();
    var jewel = (cart.items || []).find(function (i) { return i.key === jewelKey; });
    if (!jewel) throw new Error("Jewel no longer in cart.");

    var groups = buildGroupMap(cart);
    var sourceG = groups.get(sourceUid);
    var targetG = groups.get(targetUid);

    var sourceHadUpgrade = !!(sourceG && sourceG.hasUpgrade);
    var targetHasUpgrade = !!(targetG && targetG.hasUpgrade);
    var sourceLastJewel = !!(sourceG && sourceG.jewelCount === 1);

    // Cross-plan transfer: source upgrade about to be orphaned + target
    // has no upgrade → reassign source upgrade to target so the merged
    // group remains Infinita.
    var willTransfer = sourceHadUpgrade && sourceLastJewel && !targetHasUpgrade;

    // The merged group's tier:
    var mergedTier = (targetHasUpgrade || willTransfer) ? "infinita" : "esencia";

    // (1) Move the jewel into the target group.
    //     Preserve _av_line_id (or generate one) so Shopify doesn't merge
    //     this jewel with a sibling in the target group that has the same
    //     variant + tier.
    var newJewelProps = Object.assign({}, jewel.properties || {}, {
      _av_uid: targetUid,
      _av_tier: mergedTier,
    });
    delete newJewelProps._av_upgrade;
    if (!newJewelProps._av_line_id) newJewelProps._av_line_id = uuidv4();
    await changeLineItem(jewel.key, jewel.quantity, newJewelProps);

    // (2) If we're transferring, reassign source's upgrade line item's
    //     uid to target so the upgrade now covers the merged group.
    if (willTransfer && sourceG.upgrade) {
      var upg = sourceG.upgrade;
      var newUpgProps = Object.assign({}, upg.properties || {}, {
        _av_uid: targetUid,
      });
      if (!newUpgProps._av_line_id) newUpgProps._av_line_id = uuidv4();
      await changeLineItem(upg.key, upg.quantity, newUpgProps);
    }

    // (2b) Not transferring, but the source group's own upgrade is now
    //      orphaned (both groups had an Infinita) → remove it right here.
    //      Doing it deterministically avoids a race with the cart guard,
    //      which could otherwise leave a duplicate €0 Infinita line behind.
    var willOrphan = sourceHadUpgrade && sourceLastJewel && targetHasUpgrade;
    if (willOrphan && sourceG.upgrade) {
      await changeLineItem(sourceG.upgrade.key, 0);
    }

    // (3) If we transferred AND the target group had other jewels on
    //     Esencia, rewrite them to Infinita so the group is consistent.
    //     (The guard would otherwise demote them on the next pass.)
    if (willTransfer && targetG && targetG.jewels && targetG.jewels.length > 0) {
      for (var i = 0; i < targetG.jewels.length; i++) {
        var tj = targetG.jewels[i];
        // Re-fetch the line item by key to get fresh properties
        var liveCart = await fetchCart();
        var live = (liveCart.items || []).find(function (it) { return it.key === tj.key; });
        if (!live) continue;
        if (String((live.properties || {})._av_tier || "").toLowerCase() === "infinita") continue;
        var liveNewProps = Object.assign({}, live.properties || {}, {
          _av_tier: "infinita",
        });
        if (!liveNewProps._av_line_id) liveNewProps._av_line_id = uuidv4();
        await changeLineItem(live.key, live.quantity, liveNewProps);
      }
    }
  }

  async function doLink(jewelKey, sourceUid, targetUid) {
    try {
      await mergeInto(jewelKey, sourceUid, targetUid);

      window.AVModal.toast({ message: t("toast.linked"), variant: "success" });
      document.dispatchEvent(new CustomEvent("av:cart-changed"));
      // Let the guard run (which strips duplicates / demotes orphans), then reload.
      setTimeout(function () { window.location.reload(); }, 700);
    } catch (e) {
      console.error("[av-cart-link] doLink failed", e);
      alert(e && e.message ? e.message : "Linking failed.");
    }
  }

  /**
   * Unlink: move `jewelKey` from `sourceUid` into a freshly generated new
   * uid (its own gallery). If the source group has an Infinita upgrade,
   * the separated jewel gets a NEW Infinita upgrade added so its plan is
   * preserved (matches what the customer originally paid for).
   */
  async function doUnlink(jewelKey, sourceUid) {
    try {
      var cart = await fetchCart();
      var jewel = (cart.items || []).find(function (i) { return i.key === jewelKey; });
      if (!jewel) throw new Error("Jewel no longer in cart.");

      // The flag set in av-tier.js at original purchase time. Tells us whether
      // the customer originally paid for an Infinita upgrade for THIS specific
      // jewel — not just that the current group happens to be on Infinita.
      // A jewel that was added as Esencia and later joined an Infinita group
      // via linking will NOT have this flag, so unlinking it leaves it Esencia
      // (no surprise upgrade charge on the customer's behalf).
      var hadOwnUpgrade = String((jewel.properties || {})._av_had_own_upgrade || "").toLowerCase() === "true";

      var newUid = uuidv4();
      var newTier = hadOwnUpgrade ? "infinita" : "esencia";

      // (1) Move this jewel to a fresh uid. Always ensure _av_line_id so
      //     Shopify doesn't merge it with another esencia-line of the same
      //     variant somewhere in the cart.
      var newJewelProps = Object.assign({}, jewel.properties || {}, {
        _av_uid: newUid,
        _av_tier: newTier,
      });
      delete newJewelProps._av_upgrade;
      if (!newJewelProps._av_line_id) newJewelProps._av_line_id = uuidv4();
      await changeLineItem(jewel.key, jewel.quantity, newJewelProps);

      // (2) If this jewel originally paid for Infinita, add a fresh upgrade
      //     line item for its new gallery — preserves what the customer paid
      //     for. (Skip if the jewel was an Esencia origin: don't surprise-
      //     charge them for an upgrade they didn't ask for.)
      if (hadOwnUpgrade) {
        var groups = buildGroupMap(cart);
        var sourceG = groups.get(sourceUid);
        var variantId = (sourceG && sourceG.upgrade && sourceG.upgrade.variant_id)
          || findUpgradeVariantId(cart);
        if (variantId) {
          await addLineItem(variantId, 1, {
            _av_tier: "infinita",
            _av_uid: newUid,
            _av_upgrade: "true",
            _av_line_id: uuidv4(),
          });
        } else {
          console.warn("[av-cart-link] No upgrade variant_id available — separated jewel will fall back to Esencia.");
        }
      }

      window.AVModal.toast({ message: t("toast.unlinked"), variant: "success" });
      document.dispatchEvent(new CustomEvent("av:cart-changed"));
      setTimeout(function () { window.location.reload(); }, 700);
    } catch (e) {
      console.error("[av-cart-link] doUnlink failed", e);
      alert(e && e.message ? e.message : "Unlink failed.");
    }
  }

  window.AVCartLink = { mergeInto: mergeInto, buildGroupMap: buildGroupMap, fetchCart: fetchCart };

  /* ── init ── */

  function init() {
    injectStyles();
    renderBadges();
    document.addEventListener("av:cart-guard-ready", renderBadges);
    document.addEventListener("av:cart-changed", function () { setTimeout(renderBadges, 300); });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
